import { CompletedShipmentStat } from "@/components/CompletedShipmentStat"
import { getCompletedShipmentStats } from "@/lib/completed-shipments/server"
import { useRouter } from "next/router"
import stateCityDictionary, {
  formatName,
  urlifyComplexName,
  urlifyName,
} from "../../../constants/stateCityDictionary"
import PageHead from "../../../components/head/PageHead"
import PageNav from "../../../storyblok/sections/PageNav"
import {
  petShippingStatesJson as petShippingStatesJsonRaw,
  ShippingStates,
} from "../../../constants/pet-shipping/petShippingStates"
import RecentPetTransports from "../../../sections/pet-shipping/RecentPetTransports"
import { stateShipmentsData } from "../../../constants/pet-shipping/stateShipments"
import { stateCityShipmentsData } from "../../../constants/pet-shipping/stateCityShipments"
import LocalPageHeader from "../../../storyblok/sections/LocalPageHeader"
import stateNeedForTransport from "../../../constants/pet-shipping/state-need-for-transport.json"
import { ReviewsContextProvider } from "../../../context/ReviewsContext"
import stateCityImages from "../../../constants/pet-shipping/state-city-images.json"
import {
  getStateShipmentResult,
  getTransportersResult,
  getCachedData,
  isIncompleteMarketplaceData,
} from "../../../services/cache"
import type { ShipmentData, TransporterRow } from "../../../services/cache"
import { withActiveProfileFlag } from "@/util/validateDriver"
import { GetServerSideProps } from "next"
import { ErrorBoundary } from "../../../components/ErrorBoundary"
import { useState } from "react"
import dynamic from "next/dynamic"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import { fetchReviews } from "@/util/reviews"
import { usePetMarketplaceData } from "@/hooks/use-pet-marketplace-data"
import type { PetShippingCostProps } from "@/sections/pet-shipping/PetShippingCost"
import { withHeroImage } from "@/util/need-for-transport"
import type { SenjaVideoTestimonial } from "@/lib/senja-video-testimonials"
import { SENJA_WIDGET_GROUPS } from "@/lib/senja-widgets"
import type { SenjaWidgetGroupKey } from "@/lib/senja-widgets"
import { getSenjaVideoTestimonials } from "@/lib/senja-video-testimonials-cache"
import PetCostSection from "@/components/cost/PetCostSection"
import PetTransportAtAGlance from "@/sections/pet-shipping/PetTransportAtAGlance"
import { parseCommittedCount } from "@/util/pet-at-a-glance"
import { petCostServiceSchema } from "@/seo/pet-rich-snippets"
import { toJsonLdString } from "@/seo/jsonld"
import { findStateCostBand } from "@/util/cost-summary"
import type {
  CostSummaryBand,
  CostSummarySnapshot,
  MarketplaceCostSummary,
} from "@/util/cost-summary"
import { computeOfferPriceStats, parsePriceToNumber } from "@/util/cost-stats"
import type { OfferPriceStats } from "@/util/cost-stats"
import costSummariesSnapshot from "@/constants/pet-shipping/cost-summaries.json"
import toolsData from "@/constants/tools/tools.json"

// Only the tool id reaches client JSX (the <noscript> link); the resolved
// calculator entry is passed via props so tools.json stays out of the bundle.
const SenjaVideoTestimonials = dynamic(() =>
  import("@/components/senja/SenjaVideoTestimonials").then(
    (module) => module.SenjaVideoTestimonials,
  ),
)

const PET_COST_CALCULATOR_ID = "pet-shipping-cost-calculator"
// Location-free testimonials, so the copy never contradicts the page's place.
const SENJA_GROUP: SenjaWidgetGroupKey = "petLocationFree"

// State-scope cost data: no city label (the band/schema serve the whole state).
interface PetCostData {
  costBand: CostSummaryBand | null
  marketplaceCost: MarketplaceCostSummary | null
  costDataWindow: { from: string; to: string } | null
  offerPriceStats: OfferPriceStats | null
  // Committed marketplace total for this state; null when the dataset has no
  // entry. Feeds the at-a-glance block only.
  shipmentsPosted: number | null
  calculator: {
    iframeUrl: string
    title: string
    responsiveHeight?: {
      xs?: string
      sm?: string
      md?: string
      lg?: string
      xl?: string
    }
  }
  stateLabel: string
  petUrl: string
}

interface DataItem {
  state: string
  data: string
  services: string
}

interface StateShipments {
  state: string
  name: string
  shipmentPosted: string
}

interface CityData {
  state: string
  shipmentPosted: string
  city: string
  cityState: string
}

type Transporter = TransporterRow & { hasActiveProfile?: boolean }

interface IPetShippingProps {
  stateData: ShippingStates["description"]
  metaTitle: ShippingStates["metaTitle"]
  metaDescription: ShippingStates["metaDescription"]
  propsHeading: ShippingStates["propsHeading"]
  propsSubheading: ShippingStates["propsSubheading"]
  propsDescription: ShippingStates["propsDescription"]

  linksData: { header: string; link: string; subtitle: string }[]
  citiesLinksData: { header: string; link: string; subtitle: string }[]
  needForTransportData: Record<string, DataItem[]>
  initialShipments: ShipmentData
  initialTransporters: Transporter[]
  isPrunedState: boolean
  petCost: PetCostData | null
  senjaTestimonials: SenjaVideoTestimonial[]
}

// Call dynamic() with a literal import() at module scope. Next's SWC transform
// only attaches chunk metadata to direct calls, and that metadata is what lets
// the client preload each server-rendered chunk before hydration. Wrapping
// these calls in a helper hid them from the transform, so the client hydrated
// an empty loading state against server markup (React #418, MAR-81). The
// default loading state already renders null, so no options are needed.
const Footer = dynamic(() => import("../../../storyblok/static/Footer"))
const LinkBlock = dynamic(() => import("../../../components/link-block"))
const PetShippingBlogLinks = dynamic(
  () => import("../../../sections/pet-shipping/PetShippingBlogLinks"),
)
const PetShippingCost = dynamic<PetShippingCostProps>(
  () => import("../../../sections/pet-shipping/PetShippingCost"),
)
const PetTransportCoverage = dynamic(
  () => import("../../../sections/pet-shipping/PetTransportCoverage"),
)
const PetTransportLaws = dynamic(
  () => import("../../../sections/pet-shipping/PetTransportLaws"),
)
const PetTransportWays = dynamic(
  () => import("../../../sections/pet-shipping/PetTransportWays"),
)
const TopTransporters = dynamic(
  () => import("../../../sections/pet-shipping/TopTransporters"),
)
const PetShippingEntityBlock = dynamic(
  () => import("../../../sections/pet-shipping/PetShippingEntityBlock"),
)

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const findHeroImage = (state: string) => {
  const stateSlug = urlifyName(state)

  const image = stateCityImages.find(
    (img) => img.page_type === "state" && img.state_slug === stateSlug,
  )

  return image
    ? {
        url: image.hero_image_url,
        alt: image.hero_image_alt_text,
        width: parseInt(image.hero_image_width),
        height: parseInt(image.hero_image_height),
      }
    : null
}

export default function PetShippingLegacy({
  stateData,
  linksData,
  citiesLinksData,
  needForTransportData,
  initialShipments,
  initialTransporters,
  isPrunedState,
  petCost,
  senjaTestimonials,
}: IPetShippingProps) {
  const router = useRouter()
  const { state } = router.query
  const formattedState = formatName(state as string)

  const heroImage = findHeroImage(state as string)

  const insertHeroImageAfterH2 = (html: string): string => {
    if (!heroImage) {
      return html
    }

    const maxWidth = 600
    const scaledWidth = Math.min(heroImage.width, maxWidth)
    const scaledHeight = Math.floor(
      (scaledWidth * heroImage.height) / heroImage.width,
    )

    const heroImageHtml = `
      <div class="flex justify-center my-8 max-w-[1189px] mx-auto">
        <img 
          src="${heroImage.url}" 
          alt="${heroImage.alt}"
          width="${scaledWidth}"
          height="${scaledHeight}"
          class="rounded-lg shadow-lg"
        />
      </div>
    `

    // Find the first closing h2 tag and insert the hero image after it
    const h2ClosingTagIndex = html.indexOf("</h2>")

    if (h2ClosingTagIndex === -1) {
      // If no h2 tag found, add one at the beginning
      const defaultTitle = `Discover ${formattedState}`

      return `<h2 class="text-center text-blue7 font-montserrat text-3xl mb-10">${defaultTitle}</h2>${heroImageHtml}${html}`
    }

    return (
      html.slice(0, h2ClosingTagIndex + 5) +
      heroImageHtml +
      html.slice(h2ClosingTagIndex + 5)
    )
  }

  const breadcrumbSchemaJSON = toJsonLdString({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Pet Transportation",
        item: "https://citizenshipper.com/pet-transportation",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "All Cities",
        item: "https://citizenshipper.com/pet-shipping/all-cities",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: `${formattedState} Pet Shipping`,
        item: `https://citizenshipper.com/pet-shipping/${state}`,
      },
    ],
  })

  const serviceSchemaJSON = toJsonLdString({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${formattedState} Pet Transport`,
    description: `CitizenShipper connects ${formattedState} pet owners with background-checked, independent contractor drivers for domestic ground pet transport.`,
    url: `https://citizenshipper.com/pet-shipping/${state}`,
    provider: {
      "@type": "Organization",
      name: "CitizenShipper",
      url: "https://citizenshipper.com",
    },
    areaServed: {
      "@type": "AdministrativeArea",
      name: formattedState,
      containedInPlace: {
        "@type": "Country",
        name: "United States",
      },
    },
    serviceType: "Pet Transportation Marketplace",
    offers: {
      "@type": "AggregateOffer",
      lowPrice: "245",
      priceCurrency: "USD",
    },
  })

  const faqSchemaJSON = toJsonLdString({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What do you need for pet shipping to or from ${formattedState}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "You need a Certificate of Veterinary Inspection issued within 10 days of travel, a rabies vaccination certificate issued within 30 days before transport, and a pet carrier that allows your pet to stand, sit, and turn around with adequate ventilation, a solid bottom, and a secure closure. Puppies and kittens need carriers with padded walls.",
        },
      },
      {
        "@type": "Question",
        name: `What determines the cost of pet shipping in ${formattedState}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Three factors determine cost: the method of transport (ground vs. air), the distance and timing (ground transport charges per-mile rates, and summer and holidays cost more due to high demand), and whether you select VIP service for solo pet transport.",
        },
      },
      {
        "@type": "Question",
        name: `How to find the best pet transport service in ${formattedState}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "On CitizenShipper, review driver profiles for experience, qualifications like USDA T Registration, and customer reviews. Use the internal messaging system to discuss trip details directly with drivers before booking. Compare multiple quotes rather than choosing on price alone.",
        },
      },
    ],
  })

  const props = {
    heading: `${stateData.propsHeading}`,
    subheading: `${stateData.propsSubheading}`,
    description: `${stateData.propsDescription}`,
    smBgImage: {
      alt: "Pet Shipping Services",
      filename:
        "https://a-us.storyblok.com/f/1002522/1017x877/93579a1ac6/pets-transport.png",
    },
    hasLongDescription: true,
    buttonLabel: "Get Free Quotes from Drivers",
    buttonLink: "shipments/new/animals",
    image: {
      alt: "Pet Shipping Services",
      filename:
        "https://a-us.storyblok.com/f/1002522/1017x877/93579a1ac6/pets-transport.png",
    },
  }
  const items = [
    { name: "Home", href: "/" },
    { name: "Pets", href: "/pet-transportation" },
    { name: "All Cities", href: "/pet-shipping/all-cities" },
    {
      name: formattedState as string,
      href: `/pet-shipping/${state}`,
    },
  ]
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation
  const stateShippingData = needForTransportData[formattedState]
  const [expandedSections, setExpandedSections] = useState({
    cities: false,
    states: false,
  })

  const toggleSection = (section: "cities" | "states") => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const {
    exportShipments,
    importShipments,
    transporters,
  } = usePetMarketplaceData(
    { scope: "state", stateAbbrev: stateAbbrev ?? "" },
    initialShipments,
    initialTransporters,
  )

  return (
    <ReviewsContextProvider>
      <PageNav noBlok buttonLabel="Get Free Quotes from Drivers" />
      <LocalPageHeader
        {...props}
        items={items}
        shipmentProof={
          <CompletedShipmentStat concept="pets" period="last12Months">
            {(_count, plain) =>
              `At least ${plain} pets safely transported nationwide last year.`
            }
          </CompletedShipmentStat>
        }
      />
      {/* Directly under the hero's quote CTA and above the long-form content,
          per MAR-78. Same gating as the cost section it summarizes. */}
      {!isPrunedState && petCost && (
        <PetTransportAtAGlance
          headingLabel={petCost.stateLabel}
          stateLabel={petCost.stateLabel}
          costBand={petCost.costBand}
          marketplaceCost={petCost.marketplaceCost}
          costDataWindow={petCost.costDataWindow}
          shipmentsPosted={petCost.shipmentsPosted}
        />
      )}
      <PageHead
        title={stateData.metaTitle}
        description={stateData.metaDescription}
        ogImage={
          heroImage?.url ||
          "https://a-us.storyblok.com/f/1002522/1017x877/93579a1ac6/pets-transport.png/m/"
        }
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: breadcrumbSchemaJSON }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serviceSchemaJSON }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: faqSchemaJSON }}
        />
      </PageHead>

      <section className="relative pt-0 xl:mt-2 py-2 px-4 lg:px-10 lg:mx-auto lg:max-w-[1300px]">
        <ErrorBoundary>
          <RecentPetTransports
            topExportShipments={exportShipments}
            topImportShipments={importShipments}
          />
        </ErrorBoundary>
        {!isPrunedState && petCost && (
          <>
            <PetCostSection
              stateLabel={petCost.stateLabel}
              costBand={petCost.costBand}
              marketplaceCost={petCost.marketplaceCost}
              costDataWindow={petCost.costDataWindow}
              calculator={petCost.calculator}
              calculatorToolId={PET_COST_CALCULATOR_ID}
            />
            {petCost.offerPriceStats && (
              <script
                key="pet-shipping-state-cost-service-schema"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: toJsonLdString(
                    petCostServiceSchema({
                      state: petCost.stateLabel,
                      petUrl: petCost.petUrl,
                      priceStats: petCost.offerPriceStats,
                    }),
                  ),
                }}
              />
            )}
          </>
        )}
        {!isPrunedState && (
          <PetTransportLaws needForTransport={needForTransportData} />
        )}
        {!isPrunedState && <PetShippingCost costEntry={null} />}
      </section>
      {!isPrunedState && <PetTransportWays />}
      <section className="max-w-[1189px] mx-auto px-4 my-10">
        {!isPrunedState && <PetTransportCoverage />}
        <TopTransporters topTransporters={transporters} />
        <SenjaVideoTestimonials
          testimonials={senjaTestimonials}
          heading={`What Pet Owners Say About Shipping Pets To & From ${formattedState}`}
          widgetID={SENJA_WIDGET_GROUPS[SENJA_GROUP].widgetId}
        />
        {stateShippingData && !isPrunedState && (
          <section>
            <div className="prose-headings:font-merriweather prose-h3:text-2xl prose-h2:text-center prose-h3:mt-4 prose-p:text-lg prose-p:m-0 prose-p:py-1.5">
              {safelyRenderHtml(stateShippingData[0]?.services || "")}
            </div>
          </section>
        )}
        {!isPrunedState && (
          <section className="pt:24 prose-h2:font-merriweather prose-h3:font-merriweather prose-h2:text-3xl prose-h2:mb-10 prose-h2:text-center prose-h2:mt-12 prose-p:text-lg prose-h3:mb-6  prose-h3:mt-8 prose-p:mb-4">
            {safelyRenderHtml(insertHeroImageAfterH2(stateData.description))}
          </section>
        )}
        {!isPrunedState && <PetShippingBlogLinks />}
        <PetShippingEntityBlock />
      </section>
      <section className="mb-8 -mt-8">
        <div className="mx-auto p-4 md:max-w-[600px] lg:max-w-[1240px] text-center">
          <hr
            style={{ width: "30%", margin: "0 auto", marginBottom: "2rem" }}
          />
          <div className="mb-6">
            <button
              onClick={() => toggleSection("cities")}
              className="flex items-center justify-between w-full bg-white p-3 rounded shadow-sm hover:bg-gray-50 transition-colors"
              aria-expanded={expandedSections.cities}
              aria-controls="cities-links"
            >
              <hr />
              <h2 className="text-md font-merriweather m-0 text-left">
                Popular Pet Transport Destinations in {formattedState}
              </h2>
              <svg
                className={`w-5 h-5 transition-transform ${
                  expandedSections.cities ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            <div
              id="cities-links"
              className={`transition-all duration-300 overflow-hidden ${
                expandedSections.cities
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
              role="region"
              aria-labelledby="cities-heading"
            >
              <div className="py-3">
                <LinkBlock
                  blok={{
                    title: "",
                    items: citiesLinksData,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <button
              onClick={() => toggleSection("states")}
              className="flex items-center justify-between w-full bg-white p-3 rounded shadow-sm hover:bg-gray-50 transition-colors"
              aria-expanded={expandedSections.states}
              aria-controls="states-links"
            >
              <hr />
              <h2 className="text-md font-merriweather m-0 text-left">
                All Pet Transport Destination States Across the US
              </h2>
              <svg
                className={`w-5 h-5 transition-transform ${
                  expandedSections.states ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            <div
              id="states-links"
              className={`transition-all duration-300 overflow-hidden ${
                expandedSections.states
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
              role="region"
              aria-labelledby="states-heading"
            >
              <div className="py-3">
                <LinkBlock
                  blok={{
                    title: "",
                    items: linksData,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer noBlok />
    </ReviewsContextProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const { state } = params as { state: string }
  const formattedState = formatName(state)

  // Validate state exists
  if (!stateCityDictionary[formattedState]) {
    return { notFound: true }
  }

  // Memoized process-wide; never throws. Kept outside the try/catch so the
  // fallback props below carry testimonials too.
  const senjaTestimonials = await getSenjaVideoTestimonials(SENJA_GROUP)

  try {
    const pageData = await getCachedData(`page:static:${state}`, async () => {
      const formattedState = formatName(state)

      // Fetch review stats inside cache so they're consistent with the description
      const reviewsData = await fetchReviews()
      const rawTotalReviews = reviewsData?.totalReviews ?? "48600"
      const totalReviews = Number(
        rawTotalReviews.replace(/,/g, ""),
      ).toLocaleString("en-US")
      const avgRating = reviewsData?.averageRating ?? "4.8"

      const stateData = petShippingStatesJsonRaw[state as keyof ShippingStates]

      // Template-level SEO overrides — dynamic, location-specific meta/headings
      const seoTitle = `${formattedState} Pet Transport & Shipping Services | CitizenShipper`
      const seoDescription = `Ship your dog or cat to or from ${formattedState} with background-checked drivers. Free to list, get quotes in minutes. $1K pet protection on every booking.`
      const seoHeading = `Pet Transport to and from ${formattedState}`
      const seoSubheading = `How ${formattedState} Pet Shipping Works on CitizenShipper`
      const seoPropsDescription = `CitizenShipper is a pet transportation marketplace where ${formattedState} pet owners post a free listing and receive quotes from background-checked drivers within minutes. The platform, founded in 2008, has a ${avgRating}-star rating from ${totalReviews} reviews and includes $1,000 pet protection coverage on every booking.`

      return {
        stateData: {
          metaTitle: stateData?.metaTitle || seoTitle,
          metaDescription: stateData?.metaDescription || seoDescription,
          propsHeading: stateData?.propsHeading || seoHeading,
          propsSubheading: stateData?.propsSubheading || seoSubheading,
          propsDescription: stateData?.propsDescription || seoPropsDescription,
          description: stateData?.description || "",
        },
        linksData: getStateLinks(stateShipmentsData, state),
        citiesLinksData: getStateCities(stateCityShipmentsData, state),
      }
    })

    const weeklyData = await getCachedData(`page:weekly:${state}`, async () => {
      const formattedState = formatName(state)

      return {
        needForTransportData: {
          [formattedState]: withHeroImage(
            stateNeedForTransport.filter(
              (item) => item.state.toLowerCase() === state.toLowerCase(),
            ),
          ),
        },
      }
    })

    const frequentData = await getCachedData(
      `page:frequent:${state}`,
      async () => {
        const formattedState = formatName(state)
        const stateAbbrev = stateCityDictionary[
          formattedState
        ]?.abbreviation?.toLowerCase()

        try {
          const [shipments, transporters] = await Promise.all([
            getStateShipmentResult(stateAbbrev),
            getTransportersResult("state", stateAbbrev),
          ])

          return {
            initialShipments: shipments.data,
            // Raw rows only — re-annotate after the page-cache read below.
            initialTransporters: transporters.data,
            upstreamFailed: shipments.failed || transporters.failed,
          }
        } catch (error: any) {
          console.error("ShipmentService call failed for state:", {
            state,
            error: error.message,
          })

          return {
            initialShipments: {
              exportShipments: [],
              importShipments: [],
            },
            initialTransporters: [],
            upstreamFailed: true,
          }
        }
      },
      // Failed Daedalus fetches are retried after minutes; empty 200s are valid.
      { isIncomplete: isIncompleteMarketplaceData },
    )
    const initialTransporters = await withActiveProfileFlag(
      frequentData.initialTransporters || [],
    )

    // Determine if this state is pruned (server-side only)
    type PrunedState = { state: string }
    const prunedStates = (
      await import("../../../constants/pet-shipping/prunedStates.json")
    ).default as PrunedState[]
    const isPrunedState = prunedStates.some((entry) => entry.state === state)

    // Cost section at state scope: band/trend from the committed pet snapshot
    // (state -> marketplace fallback), price range from the shipments this page
    // renders. See docs/adr/0003-pet-cost-snapshot-under-ssr.md.
    const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""
    const snapshot = costSummariesSnapshot as CostSummarySnapshot
    const costBand = findStateCostBand(snapshot, stateAbbrev)
    const marketplaceCost = snapshot.marketplace
    const costDataWindow =
      costBand || marketplaceCost ? snapshot.dataWindow : null
    const offerPriceStats = computeOfferPriceStats(
      (frequentData.initialShipments?.exportShipments ?? []).map(
        (s: { price?: string | number | null }) => ({
          winning_bid_amount: parsePriceToNumber(s.price),
        }),
      ),
      (frequentData.initialShipments?.importShipments ?? []).map(
        (s: { price?: string | number | null }) => ({
          winning_bid_amount: parsePriceToNumber(s.price),
        }),
      ),
    )
    const calculator = toolsData[PET_COST_CALCULATOR_ID]
    // Same committed dataset the state's own shipment copy draws on, keyed by
    // the abbreviation the file uses.
    const shipmentsPosted = parseCommittedCount(
      stateShipmentsData.find((item) => item.state === stateAbbrev)
        ?.shipmentPosted,
    )
    const petCost: PetCostData = {
      costBand,
      marketplaceCost,
      costDataWindow,
      offerPriceStats,
      shipmentsPosted,
      calculator: {
        iframeUrl: calculator.iframeUrl,
        title: calculator.title,
        // Omit (don't set to undefined) when absent: Next refuses to serialize
        // `undefined` in getServerSideProps props.
        ...(calculator.responsiveHeight
          ? { responsiveHeight: calculator.responsiveHeight }
          : {}),
      },
      stateLabel: formattedState,
      petUrl: `https://citizenshipper.com/pet-shipping/${state}`,
    }

    return {
      props: {
        completedShipments: await getCompletedShipmentStats(),
        ...pageData,
        ...weeklyData,
        ...frequentData,
        initialTransporters,
        isPrunedState,
        petCost,
        senjaTestimonials,
      },
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("not found")) {
      return { notFound: true }
    }

    return {
      props: {
        completedShipments: await getCompletedShipmentStats(),
        stateData: {
          metaTitle: `${formatName(
            state,
          )} Pet Transport & Shipping Services | CitizenShipper`,
          metaDescription: `Ship your dog or cat to or from ${formatName(
            state,
          )} with background-checked drivers. Free to list, get quotes in minutes. $1K pet protection on every booking.`,
          propsHeading: `Pet Transport to and from ${formatName(state)}`,
          propsSubheading: `How ${formatName(
            state,
          )} Pet Shipping Works on CitizenShipper`,
          propsDescription: `CitizenShipper is a pet transportation marketplace where ${formatName(
            state,
          )} pet owners post a free listing and receive quotes from background-checked drivers within minutes. The platform, founded in 2008, has a 4.8-star rating from 48,600+ reviews and includes $1,000 pet protection coverage on every booking.`,
          description: "",
        },
        linksData: [],
        citiesLinksData: [],
        needForTransportData: {},
        initialShipments: { exportShipments: [], importShipments: [] },
        initialTransporters: [],
        petCost: null,
        senjaTestimonials,
      },
    }
  }
}

function getStateLinks(obj: StateShipments[], state: string) {
  return obj
    .filter((item) => item.state !== state)
    .map((key) => ({
      header: key.name,
      subtitle: `${key.shipmentPosted} Shipments`,
      link: `/pet-shipping/${urlifyName(key.name)}`,
    }))
}

function getStateCities(obj: CityData[], state: string) {
  return obj
    .filter((item) => item.state === formatName(state))
    .map((item) => ({
      header: item.cityState,
      subtitle: `${item.shipmentPosted} Shipments`,
      link: `/pet-shipping/${urlifyName(item.state)}/${urlifyName(item.city)}`,
    }))
}

function getStateNeedForTransport(
  data: Record<string, DataItem[]>,
  state: string,
): DataItem[] {
  return data[state] || []
}
